import { useState } from 'react';
import { toast } from 'react-toastify';
import { collateralApi, ApiError } from '../services/collateralApi';
import {
    CommodityCulture,
    LegalRegime,
    VerificationStatus,
    MAX_OWNERS,
} from '../types/Commodity';
import type {
    CommodityOwner,
    CommodityDelivery,
    CommodityVerification,
} from '../types/Commodity';
import type { ErrorField } from '../types/ErrorField';

export default function useCommodityPageForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
    const [isCreating, setIsCreating] = useState(false);
    const [errorFields, setErrorFields] = useState<ErrorField[]>([]);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [legalRegimes, setLegalRegimes] = useState<LegalRegime[]>([]);
    const [custodyAddress, setCustodyAddress] = useState('');
    const [custodyCity, setCustodyCity] = useState('');
    const [custodyState, setCustodyState] = useState('');
    const [farmName, setFarmName] = useState('');
    const [plantedArea, setPlantedArea] = useState<number | ''>('');
    const [areaUnit, setAreaUnit] = useState('');
    const [areaOwner, setAreaOwner] = useState('');
    const [leasedArea, setLeasedArea] = useState(false);
    const [registrationNumber, setRegistrationNumber] = useState('');
    const [registryOffice, setRegistryOffice] = useState('');
    const [registryState, setRegistryState] = useState('');
    const [warehouseNotified, setWarehouseNotified] = useState(false);
    const [comments, setComments] = useState('');

    const [deliveries, setDeliveries] = useState<CommodityDelivery[]>([
        { culture: CommodityCulture.SOYBEAN, harvest: '', bags: null, weightKg: null, grade: '' },
    ]);
    const [verifications, setVerifications] = useState<CommodityVerification[]>([]);
    const [owners, setOwners] = useState<CommodityOwner[]>([]);

    const [ownerSearch, setOwnerSearch] = useState('');
    const [ownerResults, setOwnerResults] = useState<any[]>([]);
    const [isSearchingOwner, setIsSearchingOwner] = useState(false);
    const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);

    const searchOwner = async (query: string) => {
        if (!query || query.length < 3) {
            setOwnerResults([]);
            setShowOwnerDropdown(false);
            return;
        }
        setIsSearchingOwner(true);
        try {
            const [legalResults, individualResults] = await Promise.all([
                collateralApi.searchLegalPerson(query).catch(() => []),
                collateralApi.searchPerson(query).catch(() => []),
            ]);
            const normalized = [
                ...legalResults.map((p: any) => ({
                    personId: p.personId || p.uuid || p.id || '',
                    name: p.legalName || p.fantasyName || p.name || '',
                    document: (p.govTaxId || '').replace(/\D/g, ''),
                    documentFormatted: p.govTaxIdFormatted || p.govTaxId || '',
                    type: 'PJ',
                })),
                ...individualResults.map((p: any) => ({
                    personId: p.id || p.personId || '',
                    name: p.name || p.fullName || '',
                    document: (p.socialId || '').replace(/\D/g, ''),
                    documentFormatted: p.socialId || '',
                    type: 'PF',
                })),
            ];
            setOwnerResults(normalized);
            setShowOwnerDropdown(normalized.length > 0);
        } catch {
            setOwnerResults([]);
        } finally {
            setIsSearchingOwner(false);
        }
    };

    const handleSelectOwner = (person: any) => {
        addOwner({ personId: person.personId, name: person.name, document: person.document });
        setOwnerSearch('');
        setOwnerResults([]);
        setShowOwnerDropdown(false);
    };

    const addDelivery = () =>
        setDeliveries(prev => [
            ...prev,
            { culture: CommodityCulture.SOYBEAN, harvest: '', bags: null, weightKg: null, grade: '' },
        ]);
    const removeDelivery = (index: number) =>
        setDeliveries(prev => prev.filter((_, i) => i !== index));
    const updateDelivery = (index: number, patch: Partial<CommodityDelivery>) =>
        setDeliveries(prev => prev.map((d, i) => (i === index ? { ...d, ...patch } : d)));

    const addVerification = () =>
        setVerifications(prev => [
            ...prev,
            { verificationType: 'PLANTING_1', status: VerificationStatus.PENDING },
        ]);
    const removeVerification = (index: number) =>
        setVerifications(prev => prev.filter((_, i) => i !== index));
    const updateVerification = (index: number, patch: Partial<CommodityVerification>) =>
        setVerifications(prev => prev.map((v, i) => (i === index ? { ...v, ...patch } : v)));

    const isDuplicateOwner = (list: CommodityOwner[], owner: CommodityOwner) =>
        !!owner.personId && list.some(o => o.personId === owner.personId);

    const addOwner = (owner: CommodityOwner) => {
        if (owners.length >= MAX_OWNERS) {
            toast.error(`Máximo de ${MAX_OWNERS} proprietários permitido`);
            return;
        }
        if (isDuplicateOwner(owners, owner)) {
            toast.error('Proprietário já adicionado');
            return;
        }
        setOwners(prev =>
            prev.length >= MAX_OWNERS || isDuplicateOwner(prev, owner) ? prev : [...prev, owner]
        );
    };
    const removeOwner = (index: number) => setOwners(prev => prev.filter((_, i) => i !== index));

    const toggleLegalRegime = (regime: LegalRegime) =>
        setLegalRegimes(prev =>
            prev.includes(regime) ? prev.filter(r => r !== regime) : [...prev, regime]
        );

    const validate = (): ErrorField[] => {
        const errors: ErrorField[] = [];
        if (!description.trim()) {
            errors.push({ fieldName: 'description', message: 'Campo obrigatório' });
        }
        if (legalRegimes.length < 1 || legalRegimes.length > 2) {
            errors.push({ fieldName: 'legalRegimes', message: 'Selecione 1 ou 2 regimes jurídicos' });
        }
        if (deliveries.length < 1) {
            errors.push({ fieldName: 'deliveries', message: 'Informe ao menos uma entrega' });
        }
        const invalidHarvest = deliveries.some(d => !/^\d{4}\/\d{4}$/.test(d.harvest));
        if (invalidHarvest) {
            errors.push({ fieldName: 'deliveries', message: 'Safra deve estar no formato AAAA/AAAA' });
        }
        const missingQuantity = deliveries.some(d => d.bags == null && d.weightKg == null);
        if (missingQuantity) {
            errors.push({ fieldName: 'deliveries', message: 'Informe sacas ou quilogramas em cada entrega' });
        }
        if (owners.length < 1) {
            errors.push({ fieldName: 'owners', message: 'Informe ao menos um proprietário' });
        }
        return errors;
    };

    const handleCreate = async () => {
        const validationErrors = validate();
        if (validationErrors.length > 0) {
            setErrorFields(validationErrors);
            toast.error('Há campos obrigatórios!');
            return;
        }
        setIsCreating(true);
        setErrorFields([]);
        try {
            await collateralApi.createCommodity({
                description,
                legal_regimes: legalRegimes,
                custody_address: custodyAddress || undefined,
                custody_city: custodyCity || undefined,
                custody_state: custodyState ? custodyState.toUpperCase() : undefined,
                farm_name: farmName || undefined,
                planted_area: plantedArea === '' ? undefined : plantedArea,
                area_unit: areaUnit || undefined,
                area_owner: areaOwner || undefined,
                leased_area: leasedArea,
                registration_number: registrationNumber || undefined,
                registry_office: registryOffice || undefined,
                registry_state: registryState ? registryState.toUpperCase() : undefined,
                warehouse_notified: warehouseNotified,
                name: name || undefined,
                comments: comments || undefined,
                deliveries: deliveries.map(d => ({
                    culture: d.culture,
                    harvest: d.harvest,
                    bags: d.bags == null ? undefined : d.bags,
                    weight_kg: d.weightKg == null ? undefined : d.weightKg,
                    grade: d.grade || undefined,
                })),
                verifications: verifications.map(v => ({
                    verification_type: v.verificationType,
                    scheduled_date: v.scheduledDate || undefined,
                    verified_date: v.verifiedDate || undefined,
                    responsible: v.responsible || undefined,
                    status: v.status || undefined,
                    notes: v.notes || undefined,
                })),
                owners: owners.map(o => ({
                    person_id: o.personId,
                    name: o.name,
                    document: o.document,
                })),
            });
            onCreated();
            onClose();
        } catch (e: any) {
            if (e instanceof ApiError && e.status === 422 && e.data?.errors) {
                toast.error(e.data.message || 'Há campos obrigatórios!');
                setErrorFields(e.data.errors);
            } else if (e instanceof ApiError && e.status === 409) {
                toast.error(e.data?.message || e.message);
            } else {
                toast.error(e?.message || 'Erro ao cadastrar grãos');
            }
        } finally {
            setIsCreating(false);
        }
    };

    return {
        isCreating, errorFields,
        name, setName,
        description, setDescription,
        legalRegimes, toggleLegalRegime,
        custodyAddress, setCustodyAddress,
        custodyCity, setCustodyCity,
        custodyState, setCustodyState,
        farmName, setFarmName,
        plantedArea, setPlantedArea,
        areaUnit, setAreaUnit,
        areaOwner, setAreaOwner,
        leasedArea, setLeasedArea,
        registrationNumber, setRegistrationNumber,
        registryOffice, setRegistryOffice,
        registryState, setRegistryState,
        warehouseNotified, setWarehouseNotified,
        comments, setComments,
        deliveries, addDelivery, removeDelivery, updateDelivery,
        verifications, addVerification, removeVerification, updateVerification,
        owners, addOwner, removeOwner,
        ownerSearch, setOwnerSearch,
        ownerResults, isSearchingOwner, showOwnerDropdown,
        searchOwner, handleSelectOwner,
        handleCreate,
    };
}
